import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  dismissible?: boolean;
  /** On mobile: anchor the dialog to the bottom of the viewport (bottom-sheet
   *  pattern) so the page header remains visible above. Has no effect on sm+. */
  mobileSheet?: boolean;
}

export const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  children,
  dismissible = true,
  mobileSheet = false,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [backdropVisible, setBackdropVisible] = useState(false);

  useEffect(() => {
    if (!isOpen) { setBackdropVisible(false); return; }
    const id = requestAnimationFrame(() => setBackdropVisible(true));
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Trap focus inside dialog
  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;

    const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])',
    );

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    const trap = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", trap);
    first?.focus();

    return () => {
      document.removeEventListener("keydown", trap);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const body = (
    <div
      className={`fixed inset-0 z-50 flex justify-center ${mobileSheet ? "items-end sm:items-center" : "items-center"}`}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`fixed inset-0 bg-scrim transition-opacity duration-200 ${backdropVisible ? "opacity-100" : "opacity-0"}`}
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        className="relative z-50 rounded-lg bg-surface-card shadow-xl outline-none"
      >
        {children}
      </div>
    </div>
  );

  // Into `body`, not in place: a dialog opened from inside a sticky column — the plan panel —
  // inherits that column's stacking context (sticky always makes one), and its `z-50` then
  // ranks under the site header's `z-40`, leaving the header on top of the backdrop.
  return createPortal(body, document.body);
};
